import React, { useState, MouseEvent, useMemo, memo, ReactNode } from 'react'
import cx from 'classnames'
import { useAppSelector } from 'reduxTyped'
import { l, Popover, Loader } from '@manychat/manyui'

import { useInitializeFields } from 'common/fields/hooks/useInitializeFields'
import { FilterGroupItem } from 'common/filter/components/Filters/FilterGroupItem'
import { GLOBAL_OPERATORS } from 'common/filter/constants'
import { filterFieldMapper } from 'common/filter/filterFieldMapper'
import { isFilterOperator } from 'common/filter/guards/isFilterOperator'
import { useFilterFields } from 'common/filter/hooks/useFilterFields'
import { useSegments } from 'common/filter/hooks/useSegments'
import { AnyFilter } from 'common/filter/interfaces'
import { getDefaultFilter } from 'common/filter/models/AudienceFilter/defaults'
import { PopoverList } from 'components/MuiPopover'

import { defaultFilterConfig, defaultTheme } from './constants'
import { FilterContext } from './FilterContext'
import { FilterContextValue, FiltersProps } from './types'
import { useFilterInternal } from './useFilterInternal'
import { propsAreEqual } from './utils'

import cm from './Filters.module.css'

const FiltersRaw = <FilterType extends AnyFilter>({
  builderId,
  className,
  config: configProp = defaultFilterConfig,
  dataTestId,
  disabled = false,
  filter: initialFilter = getDefaultFilter() as FilterType,
  nodeId,
  scenario,
  source,
  style,
  theme = defaultTheme,
  onChange,
  fieldsSelector,
}: FiltersProps<FilterType>) => {
  useInitializeFields({ scenario })

  // Filter context
  const isBuilder = Boolean(builderId)
  const fields = useAppSelector((state) => fieldsSelector(state, builderId))

  // Popover handlers
  const [operatorPopoverAnchor, setOperatorPopoverAnchor] = useState<HTMLElement | null>(null)
  const filter = useFilterInternal<FilterType>({
    source,
    initial: initialFilter,
    isBuilder,
    onChange,
  })
  const context: FilterContextValue = useMemo(
    () => ({
      isBuilder,
      nodeId,
      builderId,
      source,
      fields: fields.map(filterFieldMapper),
      config: { ...defaultFilterConfig, ...configProp },
      ...filter,
    }),
    [isBuilder, nodeId, builderId, source, fields, configProp, filter],
  )
  const handleOperatorChange = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!operatorPopoverAnchor) return
    const groupIndex = Number(operatorPopoverAnchor.dataset.index)
    const value = (event.currentTarget.dataset.value as string) || ''
    if (!isFilterOperator(value)) {
      return
    }

    filter.changeOperator({
      operator: value,
      groupIndex,
    })
    setOperatorPopoverAnchor(null)
  }

  const handleOperatorPopoverClose = () => {
    setOperatorPopoverAnchor(null)
  }

  const filterFields = useFilterFields()
  const { listState: segmentsState } = useSegments()
  if (!segmentsState.isLoaded || !filterFields.isLoaded) {
    return <Loader />
  }

  let groupOperator: string | undefined
  if (operatorPopoverAnchor) {
    const groupIndex = Number(operatorPopoverAnchor.dataset.index)
    groupOperator = filter.value.groups[groupIndex]?.operator
  }

  return (
    <FilterContext.Provider value={context}>
      <div className={className} style={style}>
        {filter.value.groups.map((group, groupIndex) => {
          if (context.config.isHiddenFirstGroup && groupIndex === 0) return null
          return (
            <FilterGroupItem
              key={String(groupIndex)}
              disabled={disabled}
              theme={theme}
              group={group}
              groupIndex={groupIndex}
              setOperatorPopoverAnchor={setOperatorPopoverAnchor}
              dataTestId={dataTestId}
            />
          )
        })}
        <Popover
          open={operatorPopoverAnchor != null}
          anchor={operatorPopoverAnchor}
          anchorOrigin="left top"
          targetOrigin="left top"
          onRequestClose={handleOperatorPopoverClose}
        >
          <PopoverList>
            {GLOBAL_OPERATORS.map((operator) => (
              <a key={operator.value} onClick={handleOperatorChange} data-value={operator.value}>
                <span
                  className={cx(cm.popoverEl, {
                    [cm.popoverActiveEL]: groupOperator === operator.value,
                  })}
                >
                  {l.getString(operator.label)}
                </span>
              </a>
            ))}
          </PopoverList>
        </Popover>
      </div>
    </FilterContext.Provider>
  )
}

export const Filters = memo(FiltersRaw, propsAreEqual) as <FilterType extends AnyFilter>(
  props: FiltersProps<FilterType>,
) => ReactNode
